<script setup>
const props = defineProps({
  // Allow passing a model that contains all properties
  model: {
    type: Object,
    default: null,
  },
  // Individual properties
  color: {
    type: String,
  },
  layout: {
    type: String,
  },
  variation: {
    type: String,
  },
  image: {
    type: String,
  },
  imageAltText: {
    type: String,
  },
  videoTitle: {
    type: String,
  },
  video: {
    type: String,
  },
  heading: {
    type: String,
  },
  summary: {
    type: String,
  },
  label: {
    type: String,
  },
  buttonUrl: {
    type: String,
  },
})

// Computed properties to handle both model and individual props
const colorValue = computed(() => props.model?.color?.value || props.color)
const layoutValue = computed(() => props.model?.layout?.value || props.layout)
const imageValue = computed(() => props.model?.aprimoImage?.url || props.image)
const imageAltTextValue = computed(() => props.model?.imageAltText?.value || props.imageAltText)
const videoTitleValue = computed(() => props.model?.videoLink?.value?.text || props.videoTitle)
const videoValue = computed(() => props.model?.videoLink?.value?.href || props.video)
const titleFor = useVideoIframeTitle()
const iframeTitle = computed(() => titleFor([videoTitleValue.value], videoValue.value))
const headingValue = computed(() => props.model?.heading?.value || props.heading)
const summaryValue = computed(() => props.model?.summary?.value || props.summary)
const labelValue = computed(() => props.model?.buttonLink?.value?.text || props.label)
const buttonUrlValue = computed(() => props.model?.buttonLink?.value?.href || props.buttonUrl)
const trustpilotArea = computed(() => props.model?.trustpilotArea?.expandedValue || null)
const mediaAlignment = computed(() => props.model?.mediaContentAlignment?.value || 'left')
const contentAlignment = computed(() => props.model?.contentAlignment?.value || 'left')
const variationValue = computed(() => imageValue.value ? 'image' : videoValue.value ? 'video' : 'none')
</script>

<template>
  <div
    class="media-feature-block block-spacing"
    :class="{
      'media-feature-block--dark dark-bg': colorValue === 'navy',
      'media-feature-block--flipped': layoutValue === 'flipped',
    }"
  >
    <div class="container-fluid">
      <div class="media-feature-block__grid rounded-xl card-shadow grid grid-cols-1 gap-[30px] lg:grid-cols-2 lg:items-center 2xl:gap-[58px] mr-auto ml-auto">
        <div
          class="media-feature-block__media flex w-full prose"
          :class="{
            'justify-start': mediaAlignment === 'left',
            'justify-center': mediaAlignment === 'center',
            'justify-end': mediaAlignment === 'right',
          }"
        >
          <NuxtImg
            v-if="variationValue === 'image'"
            provider="fastlyAprimo"
            :src="imageValue"
            class="rounded-xl aspect-[656/398] object-contain"
            :alt="imageAltTextValue"
            loading="lazy"
            :width="656"
            fit="bounds"
            quality="80"
            sizes="(max-width: 1024px) 100vw, 656px"
          />
          <iframe
            v-if="variationValue === 'video'"
            :title="iframeTitle"
            :src="videoValue"
            frameborder="0"
            allow="autoplay; encrypted-media"
            allowfullscreen
            class="aspect-video w-full video-block__video rounded-xl"
            loading="lazy"
          />
        </div>
        <div
          class="media-feature-block__content prose"
          :class="{
            'text-left': contentAlignment === 'left',
            'flex flex-col items-center text-center': contentAlignment === 'center',
            'flex flex-col items-end text-right': contentAlignment === 'right',
          }"
        >
          <h2 v-if="headingValue">
            {{ headingValue }}
          </h2>
          <div v-html="summaryValue" />
          <GenericsContentArea
            v-if="trustpilotArea"
            :model="trustpilotArea"
          />
          <UButton
            v-if="labelValue"
            :color="colorValue === 'navy' ? 'info' : 'primary'"
            variant="solid"
            :to="useCmsUrl(buttonUrlValue)"
            :locale="false"
            class="justify-center no-underline w-full sm:w-auto cursor-pointer"
            :label="labelValue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/blocks/media-feature-block.scss';
</style>
